import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Trophy, X, BookOpen, ArrowRight } from "lucide-react";
import FormationsLeaderboard from "../FormationLearner/FormationsLeaderboard";
import { getLeaderboard } from "../../actions/formationLearner.actions";
import cameleonMascot from "../../asset/cameleon-boite-a-outil.png";
import dodontMascot from "../../asset/dodont.png";
import formationMascot from "../../asset/formation-avatar.png";

const SUPPORTS = [
  {
    key: "outils-controleur",
    label: "Outils du contrôleur",
    description: "La boîte à outils du contrôleur",
    mascot: cameleonMascot,
    src: "https://view.genially.com/6787bdcef4c23ae07e8d04b1",
    title: "Outils du contrôleur",
  },
  {
    key: "do-vs-dont",
    label: "Do vs Don't",
    description: "Les bonnes pratiques 2026",
    mascot: dodontMascot,
    src: "https://view.genially.com/697c7546d5f022e6851f08c4",
    title: "Do / Don't - 2026",
  },
];

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, iframe, [tabindex]:not([tabindex="-1"])';

const SupportsRapide = () => {
  const [activeKey, setActiveKey] = useState(null);
  const [showRanking, setShowRanking] = useState(false);
  // Au moins un utilisateur a-t-il décroché un badge ? Sinon on masque le classement.
  const [hasRanking, setHasRanking] = useState(false);
  const isOpen = activeKey !== null;
  const active = SUPPORTS.find((s) => s.key === activeKey);
  const userData = useSelector((s) => s.userReducer);
  const dispatch = useDispatch();

  useEffect(() => {
    let alive = true;
    dispatch(getLeaderboard())
      .then((d) => {
        if (alive) setHasRanking((d?.me?.participants || 0) > 0);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!showRanking) return;
    const onEsc = (e) => e.key === "Escape" && setShowRanking(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [showRanking]);

  const modalRef = useRef(null);
  const closeBtnRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const closeModal = () => setActiveKey(null);

  useEffect(() => {
    if (!isOpen) return;

    lastFocusedRef.current = document.activeElement;
    closeBtnRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;

      const focusables = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      lastFocusedRef.current?.focus?.();
    };
  }, [isOpen]);

  return (
    <div className="supports-rapide">
      <div className="supports-rapide-header">
        <span className="supports-rapide-eyebrow">Accès rapide</span>
      </div>

      <div className="supports-rapide-list">
        {SUPPORTS.map((s) => (
          <button
            key={s.key}
            type="button"
            className="supports-rapide-card"
            onClick={() => setActiveKey(s.key)}
          >
            <span className="supports-rapide-card-mascot">
              <img src={s.mascot} alt="" aria-hidden="true" />
            </span>
            <span className="supports-rapide-card-text">
              <span className="supports-rapide-card-label">{s.label}</span>
              <span className="supports-rapide-card-desc">{s.description}</span>
            </span>
            <i className="fa-solid fa-arrow-right supports-rapide-card-arrow"></i>
          </button>
        ))}

        {hasRanking && (
          <button
            type="button"
            className="supports-rapide-card"
            onClick={() => setShowRanking(true)}
          >
            <span className="supports-rapide-card-mascot">
              <img src={formationMascot} alt="" aria-hidden="true" />
            </span>
            <span className="supports-rapide-card-text">
              <span className="supports-rapide-card-label">Classement</span>
              <span className="supports-rapide-card-desc">Podium &amp; vos statistiques</span>
            </span>
            <i className="fa-solid fa-arrow-right supports-rapide-card-arrow"></i>
          </button>
        )}
      </div>

      {active &&
        ReactDOM.createPortal(
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={active.label}
            onClick={closeModal}
          >
            <div
              ref={modalRef}
              className="modal-content full-width-modal"
              style={{ position: "relative" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                ref={closeBtnRef}
                className="close-button"
                onClick={closeModal}
                aria-label="Fermer"
              >
                <i className="fa-regular fa-circle-xmark"></i>
              </button>
              <h2>{active.label}</h2>
              <div style={{ width: "auto" }}>
                <div
                  style={{
                    position: "relative",
                    paddingBottom: "56.25%",
                    paddingTop: 0,
                    height: 0,
                  }}
                >
                  <iframe
                    title={active.title}
                    frameBorder="0"
                    width="1200px"
                    height="675px"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    src={active.src}
                    type="text/html"
                    allowscriptaccess="always"
                    allowFullScreen
                    scrolling="yes"
                    allownetworking="all"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showRanking &&
        ReactDOM.createPortal(
          <div
            className="lb-drawer-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowRanking(false);
            }}
          >
            <aside className="lb-drawer" role="dialog" aria-modal="true" aria-label="Classement">
              <div className="lb-drawer-head">
                <span className="lb-drawer-title">
                  <Trophy size={18} /> Classement
                </span>
                <button
                  className="lb-drawer-close"
                  onClick={() => setShowRanking(false)}
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="lb-drawer-body">
                <FormationsLeaderboard
                  firstname={userData?.firstname || ""}
                  mascot={formationMascot}
                />
              </div>
              <div className="lb-drawer-footer">
                <Link
                  to="/formations"
                  className="lb-drawer-cta"
                  onClick={() => setShowRanking(false)}
                >
                  <BookOpen size={16} /> Voir toutes les formations
                  <ArrowRight size={16} />
                </Link>
              </div>
            </aside>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SupportsRapide;
